import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Grid,
  Typography,
  Button,
  Snackbar,
  TextField,
  CircularProgress,
  LinearProgress,
  Box,
} from '@mui/material';

import { store } from 'js/core/configureStore';
import { validateCode } from 'js/library/utils/API/validateSMS.js';
import { authCode } from 'js/library/utils/API/authCode';

import { codeInputAction } from 'js/core/actions/codeInputActions.js';

function CodeInput(props) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const cellPhone = props.cellPhoneProps
    ? props.cellPhoneProps
    : store.getState().phoneInputModel.cellPhone;
  const [transactionId, setTransactionId] = useState(
    props.transactionIdProps
      ? props.transactionIdProps
      : store.getState().phoneInputModel.transactionId
  );

  const [loading, setLoading] = useState(false);
  const [renderTime, setRenderTime] = useState(0);
  const [messageSnackbar, setMessageSnackbar] = useState(t('errors.user_not_logged_in'));
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [startTimer, setStartTimer] = useState(true);
  const [resendCode, setResendCode] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [sendCodeType, setSendCodeType] = useState({ sms: false, whatsapp: true });
  const [disabledInput, setDisabledInput] = useState(false);

  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    setFirstRender(false);
    window.pathname = null;
  }, []);

  useEffect(() => {
    let timerInterval = null;
    if (timer > 0 && !canResend) {
      timerInterval = setInterval(() => {
        setTimer((timer) => timer - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timerInterval);
  }, [timer, canResend]);

  useEffect(() => {
    let progressTimer = null;

    progressTimer = setInterval(() => {
      if (renderTime < 100 && startTimer) {
        setRenderTime((renderTime) => renderTime + 0.25);
      } else {
        clearInterval(progressTimer);
        setStartTimer(false);
        setRenderTime(0);
      }
    }, 60);

    return () => clearInterval(progressTimer);
  }, [renderTime, startTimer]);

  const validateSMS = (event) => {
    event.preventDefault();
    setLoading(true);

    validateCode(code, cellPhone, transactionId)
      .then((result) => {
        if (result.data.success) {
          props.codeInputComponent(cellPhone);
        } else {
          setLoading(false);
          setOpenSnackbar(true);
          setMessageSnackbar(t('errors.try_later'));
        }
      })
      .catch((error) => {
        setLoading(false);

        if (error.response.status === 422) {
          setOpenSnackbar(true);
          setMessageSnackbar(t('errors.invalid_code'));
        } else {
          setOpenSnackbar(true);
          setMessageSnackbar(t('errors.try_later'));
        }
      });
  };

  const getNewCode = (sendCodeTypeOptions = { sms: false, whatsapp: true }) => {
    setLoadingResend(true);
    setCode('');
    setSendCodeType(sendCodeTypeOptions);

    // Define o sendType baseado na escolha do usuário
    let sendType;
    if (sendCodeTypeOptions.sms) {
      sendType = { whatsapp: false, email: true, sms: true }; // SMS selecionado
    } else {
      sendType = { whatsapp: true, email: true, sms: false }; // WhatsApp selecionado
    }

    authCode(cellPhone, null, sendType)
      .then((resultAuth) => {
        if (!resultAuth.error) {
          setTransactionId(resultAuth.transactionId);
          setStartTimer(true);
          setResendCode(false);
          setLoadingResend(false);
          setTimer(30);
          setCanResend(false);
          setOpenSnackbar(true);
          setMessageSnackbar(t('general.code_sent'));
          setDisabledInput(false);
        } else {
          setLoadingResend(false);
          setOpenSnackbar(true);
          setMessageSnackbar(t('errors.try_later'));
          setDisabledInput(true);
        }
      })
      .catch(() => {
        setLoadingResend(false);
        setOpenSnackbar(true);
        setMessageSnackbar(t('errors.try_later'));
        setDisabledInput(true);
      });
  };

  if (window.pathname && !firstRender && props.success === true) {
    window.parent.location = window.pathname;
  }

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      align="center"
      className="background-login"
    >
      <Grid size={{ md: 4, sm: 6, xs: 10 }}></Grid>

      <Grid size={{ md: 4, sm: 6, xs: 10 }}>
        <Typography variant="body1">
          {sendCodeType.whatsapp
            ? t('general.you_will_receive_auth_code_in_seconds')
            : t('general.you_will_receive_auth_code_in_seconds_sms')}
          <br />
          <b>{cellPhone}</b>
        </Typography>

        <br />
        <form onSubmit={(e) => validateSMS(e)}>
          <TextField
            autoComplete="off"
            type="tel"
            autoFocus
            fullWidth
            id="code"
            value={code}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 6) {
                setCode(value);
              }
            }}
            margin="normal"
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
              inputMode: 'numeric',
              style: {
                fontSize: '1.7rem',
                letterSpacing: '6px',
                textAlign: 'center',
                fontWeight: 'bold',
                cursor: disabledInput ? 'not-allowed' : 'inherit',
              },
            }}
            placeholder="999999"
            disabled={disabledInput}
          />
          <br />
          <br />

          {canResend && !disabledInput && (
            <Typography variant="body2" style={{ paddingBottom: 30 }}>
              {t('general.didnt_receive_the_code')}
              <br />
              <span
                onClick={() => setResendCode(true)}
                style={{
                  color: '#0000FF',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {t('general.resend_sms')}
              </span>
            </Typography>
          )}

          {!canResend && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={((30 - timer) / 30) * 100}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': {
                    transition: 'transform 1s linear',
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
              >
                {t('general.wait_to_request_new_code', { timer })}
              </Typography>
            </Box>
          )}

          {resendCode && canResend && !disabledInput && (
            <Box
              sx={{
                p: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('general.botton-option-send-code')}
              </Typography>
              {loadingResend ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={30} color="primary" />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: '10px', mb: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSendCodeType({ sms: true, whatsapp: false });
                        getNewCode({ sms: true, whatsapp: false });
                      }}
                    >
                      SMS
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSendCodeType({ sms: false, whatsapp: true });
                        getNewCode({ sms: false, whatsapp: true });
                      }}
                    >
                      WhatsApp
                    </Button>
                  </Box>
                  <Button
                    color="primary"
                    fullWidth
                    variant="text"
                    onClick={() => setResendCode(false)}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </Box>
          )}

          {disabledInput && (
            <Box sx={{ mb: '20px' }}>
              <Typography variant="caption" color="error">
                {messageSnackbar}
              </Typography>
            </Box>
          )}

          <Button
            disabled={code.length !== 6 || loading || resendCode}
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
          >
            <span
              style={{
                fontWeight: '550',
                color: 'white',
                whiteSpace: 'normal',
                textTransform: 'initial',
              }}
            >
              {loading ? (
                <CircularProgress size="2rem" style={{ color: 'white' }} />
              ) : (
                <Typography variant="h6">{t('general.continue')}</Typography>
              )}
            </span>
          </Button>
        </form>
        <br />
        <br />

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={openSnackbar}
          onClose={() => setOpenSnackbar(false)}
          ContentProps={{ 'aria-describedby': 'message-id' }}
          message={<span id="message-id">{messageSnackbar}</span>}
        />

        <Grid size={{ md: 4, sm: 6, xs: 10 }}></Grid>
      </Grid>
    </Grid>
  );
}

//recebe as props dos Reducers
function mapStateToProps(state) {
  return {
    loading: state.codeInputComponent.loading,
    success: state.codeInputComponent.success,
    error: state.codeInputComponent.error,
  };
}
//envia as props para as Actions
const mapDispatchToProps = (dispatch) => ({
  codeInputComponent: (cellPhone) => codeInputAction(dispatch, cellPhone),
});

export default connect(mapStateToProps, mapDispatchToProps)(CodeInput);
